import { useState } from "react";
import styled from "styled-components";
import "react-phone-number-input/style.css";
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  RegisterOptions,
} from "react-hook-form";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";
import ar from "react-phone-number-input/locale/ar.json";
import tr from "react-phone-number-input/locale/tr.json";
import fr from "react-phone-number-input/locale/fr.json";
import { useIntl, useLocale } from "@/i18n/i18n-client";
import { isValidPhoneNumber } from "react-phone-number-input";
import { customPhoneValidation } from "@/shared/utils/custom-phone-validation";
import { CountryCode } from "libphonenumber-js/min";
import { ReactPhoneNumberInputProps } from "@/shared/globals/LazyElements/react-phone-number-input/ReactPhoneNumberInput";
import { theme as tailwindTheme } from "../../tailwind.config";
import { useStore } from "@/lib/storeData/StoreContext";

const LanguageMapper = {
  // default is "en" so no need to be handled
  ar,
  fr,
  tr,
};

interface StorePhoneInputProps
  extends Omit<ReactPhoneNumberInputProps, "onChange"> {
  defaultValue?: string | null;
  dataTest?: string;
  control: Control<any>;
  fieldName?: string;
  disabled?: boolean;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  rules?: RegisterOptions;
  hasOnlyWuiltShipment?: boolean;
}

const StorePhoneInput: React.FC<StorePhoneInputProps> = ({
  defaultValue,
  control,
  fieldName = "phone",
  disabled = false,
  error,
  rules,
  hasOnlyWuiltShipment,
  ...props
}) => {
  const { defaultCountry } = useStore();
  const language = useLocale().locale;
  const labels = LanguageMapper[language!];
  const intl = useIntl();

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  return (
    <StyledReactPhoneInput
      {...props}
      name={fieldName}
      control={control}
      rules={{
        ...rules,
        validate: (value) => {
          if (!value) {
            return true;
          }
          if (
            !isValidPhoneNumber(value || "") ||
            !customPhoneValidation(value || "", selectedCountry)
          ) {
            return intl.formatMessage({
              defaultMessage: "Invalid phone number",
              id: "HhjmvS",
            });
          }
          if (rules?.validate && typeof rules?.validate === "function") {
            return rules.validate(value, {
              name: fieldName,
            });
          }
          return true;
        },
      }}
      defaultValue={defaultValue ?? undefined}
      countryCallingCodeEditable={false}
      className={`${error ? "invalid" : ""}`}
      labels={labels}
      countryOptionsOrder={
        hasOnlyWuiltShipment ? ["EG"] : ["EG", "SA", "AE", "|", "..."]
      }
      international={true}
      defaultCountry={
        hasOnlyWuiltShipment ? "EG" : (defaultCountry as CountryCode)
      }
      countries={hasOnlyWuiltShipment ? ["EG"] : []}
      disabled={disabled}
      onCountryChange={(country) => setSelectedCountry(country as CountryCode)}
    />
  );
};

export default StorePhoneInput;

const StyledReactPhoneInput = styled(PhoneInputWithCountry)<{
  isChangeButton?: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  position: relative;
  direction: ltr;
  .PhoneInputCountry {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    padding-inline-start: 20px;
    padding-inline-end: 10px;
    height: 100%;
  }

  .PhoneInputInput {
    padding-inline-start: 64px;
  }

  .PhoneInputCountrySelectArrow {
    margin-inline-start: 8px;
    color: black;
    border-bottom-width: 1px;
    border-right-width: 1px;
    width: 0.4em;
    height: 0.4em;
    opacity: 1;
  }

  .PhoneInputCountrySelect {
    cursor: ${(props) =>
      props.disabled ? "not-allowed !important" : "pointer"};
  }

  &.invalid > input {
    outline: 1px solid ${tailwindTheme.extend.colors.error[300]} !important;
  }

  &.invalid > input:focus {
    outline: 2px solid ${tailwindTheme.extend.colors.error[500]} !important;
  }

  input {
    flex: 1 0 auto;
    background: white;
    width: 100%;
    height: 44px;
    outline: 1px solid ${tailwindTheme.extend.colors.gray[200]};
    font-size: 16px;
    box-shadow: ${tailwindTheme.extend.boxShadow.xs};
    border-radius: 8px;
    padding-top: 8px;
    transition: outline 0.2s ease-in-out;
    padding-bottom: 8px;
    font-weight: 500;
    &:focus {
      outline: 2px solid ${tailwindTheme.extend.colors.primary} !important;
    }
    &:hover {
      outline: 1px solid ${tailwindTheme.extend.colors.gray[300]};
    }
    &:disabled {
      background-color: ${tailwindTheme.extend.colors.gray[50]};
      color: ${tailwindTheme.extend.colors.gray[400]};
      cursor: not-allowed;
    }
  }
`;
